import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import ProfilePanel from './components/ProfilePanel';
import OrgPanel from './components/OrgPanel';
import NotificationSettings from './components/NotificationSettings';
import HelpPanel from './components/HelpPanel';
import { portalFromOrgCode } from './utils/portal';

type TabId = 'profile' | 'organization' | 'notifications' | 'help';

// The consolidated MAAP app is served at /app/, and its router carries the portal
// in the path: /app/{org|ssc|pcf}/... Bare /app/ only resolves once that tab holds
// the app's session data to derive the portal from, so name the portal here, the
// same way LoginPage does after a successful sign-in.
const getExternalAppPath = (orgCode: string): string =>
  `/app/${portalFromOrgCode(orgCode) || 'org'}/`;

function MainApp() {
  const { user, isLoading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [menuOpen, setMenuOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const tabs: { id: TabId; label: string; requiresOrg?: boolean }[] = [
    { id: 'organization', label: 'Manage Organization & Users', requiresOrg: true },
    { id: 'profile', label: 'My Profile' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'help', label: 'Help' },
  ];

  const visibleTabs = tabs.filter(tab => !tab.requiresOrg || user.current_organization);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Settings
          </h1>
          <div className="flex items-center gap-4">
            {user.current_organization && (
              <a
                href={getExternalAppPath(user.current_organization.code)}
                className="text-sm text-blue-600 hover:underline"
              >
                Back to MAAP
              </a>
            )}
            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {user.full_name || user.username}
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded shadow-lg z-50">
                    <button
                      onClick={() => { setMenuOpen(false); logout(); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                    >
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-4">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pr-4 py-3 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'profile' && <ProfilePanel />}
        {activeTab === 'organization' && user.current_organization && <OrgPanel />}
        {activeTab === 'notifications' && <NotificationSettings />}
        {activeTab === 'help' && <HelpPanel />}
      </main>
    </div>
  );
}

function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // If already authenticated and no OAuth params, redirect to main app
  const searchParams = new URLSearchParams(location.search);
  const hasOAuthParams = searchParams.has('return_url') || searchParams.has('client_id');

  if (isAuthenticated && !hasOAuthParams) {
    return <Navigate to="/" replace />;
  }

  return <LoginPage />;
}

function App() {
  return (
    <BrowserRouter basename="/auth">
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/" element={<MainApp />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
